import { convertToInlineStyle, getReactProps, getSplitArr } from '@m2h/common/utils/styleUtils';
import MachproHTMLElement from './common/machproHtmlElement';
import {
  installDirectionalStyleAccessors,
  normalizeDirectionalStyleText,
  setNativeStyleProperty,
} from './common/directionalStyle';

// Keep the adapter exports available for low-level runtime tests and consumers
// that historically imported the Text-local helpers.
export {
  installDirectionalStyleAccessors,
  normalizeDirectionalStyleText,
} from './common/directionalStyle';

const innerTextClassName = 'machweb-text-inner';
const linesClassName = 'machweb-text-lines';
const defaultClassName = 'machweb-text';
const defaultInnerClassName = `${innerTextClassName} ${linesClassName}`;

const INNER_ELE_NAME = 'mtext';
const textInnerForceStyleStr = 'transform: unset !important; border-radius: unset !important; background: transparent;';

// 文本行数，两处控制：
// 1.打包插件中，将max-lines转换为：-webkit-line-clamp
// 2.此处，react传入的style:max-lines, 或动态样式后有max-lines变化时处理
class Text extends MachproHTMLElement {
  // private isProcessingStyle = false; // 防重入标志
  // private lastStyleValue = ''; // 记录上次的 style 值
  resizeObserver: undefined | ResizeObserver;
  constructor() {
    super();
    // React 使用 element.style.paddingLeft/Right 更新和删除行内样式；
    // 在实例上转发到 logical property，避免删除时遗留旧值。
    installDirectionalStyleAccessors(this.style, (logicalProperty, value) => {
      this.isProcessingStyle = true;
      try {
        setNativeStyleProperty(this.style, logicalProperty, value);
        this.lastStyleValue = this.style.cssText;
      } finally {
        this.isProcessingStyle = false;
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.initContent();
    this._updataClassName();

    // 创建实际文本元素
    this.createTextElement();

    /**
     * 渲染完成事件
     * onLayout 会在 ref 的回调之前执行，导致节点的赋值失效
     * 这里 hack 一下来延迟 onLayout 的执行
     */
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this; // timeout里使用this有点问题可能回到window

    const handleDomChange = (callbacks: Function[] = []) => {
      const handleCycleCb = () => {
        const detail = self.measureInWindow();
        callbacks.forEach((fn) => {
          fn({
            detail,
          });
        });
      };
      const resizeObserver = new ResizeObserver(handleCycleCb);
      // 开始观察传入的元素
      resizeObserver.observe(self);
      this.resizeObserver = resizeObserver;
      handleCycleCb();
    };

    setTimeout(() => {
      const reactProps = getReactProps(self) || {};
      if (reactProps) {
        if (reactProps.onLayout) {
          handleDomChange([reactProps.onLayout]);
        }
        // react某些动态样式转换，在初始化时还没有值
        if (reactProps.style) {
          const reactPropsStyleStr = convertToInlineStyle(reactProps.style);
          self._updateStyle(`${self.style.cssText}${reactPropsStyleStr}`, self._updateInnerStyle.bind(self));
        }
      }
    }, 0);
  }

  disconnectedCallback() {
    this.resizeObserver && this.resizeObserver.unobserve(this);
    this.resizeObserver = undefined;
  }

  // 执行时机早于connectedCallback
  attributeChangedCallback(name, oldValue, newValue) {
    // 值无变化不执行任何动作
    if (oldValue === newValue) return;

    if (name === 'content') {
      this.updateContent();
    }
    if (name === 'className' || name === 'classname' || name === 'class') {
      this._updataClassName(newValue);
      this._updataInnerClassName(newValue);
      this._updateInnerStyle(this.style.cssText); // 样式变化，有和style相关的样式也要生效到内层
    }
    if (name === 'style') {
      this._updateStyle(newValue, this._updateInnerStyle.bind(this));
    }
  }

  static get observedAttributes() {
    return ['content', 'style', 'classname', 'className', 'class'];
  }

  // firstChild Node ✅ 包含所有类型的子节点（元素、文本、注释、空白节点等） ✅ 所有浏览器 遍历所有节点
  // firstElementChild Element ❌ 仅返回第一个元素节点 ✅ IE9+ 仅操作元素节点
  createTextElement() {
    // @ts-ignore
    if (this.firstElementChild && this.firstElementChild.localName === INNER_ELE_NAME) {
      // console.log('has textele');
      // 依赖元素就绪，且没有reactProps.style触发时，必须要有此处_getinnerStyleText执行
      // @ts-ignore
      this.firstElementChild.style.cssText = this._getinnerStyleText(this.style.cssText);
      return this.firstElementChild;
    }
    let wrapClassName = '';
    if (!this.className) {
      wrapClassName = defaultClassName;
    } else {
      wrapClassName = `${this.className} ${defaultClassName}`;
    }
    // 仅文案内容增加class
    // 并强制去除padding/margin/border/height/position: relative,top,bottom,right,left=0;
    const contentClassName = `${wrapClassName} ${defaultInnerClassName}`;

    // 创建一个 text 层
    const textElement = document.createElement(INNER_ELE_NAME);
    // 同步class和style
    textElement.className = contentClassName;
    textElement.style.cssText = this._getinnerStyleText(this.style.cssText);

    // <text>xxx</text>形式而非content形式使用的情况，将包裹的内容移动到 text 层中
    // @ts-ignore
    while (this.firstChild && this.firstChild.localName !== INNER_ELE_NAME) {
      textElement.appendChild(this.firstChild);
    }

    this.appendChild(textElement);
    return textElement;
  }

  updateContent() {
    const content = this.getAttribute('content');
    let textElement = this.firstElementChild;
    if (textElement) {
      textElement.textContent = content;
    } else {
      textElement = this.createTextElement();
      textElement.textContent = content;
    }
  }

  // 获取默认style属性对象
  _getDefaultStyleObj() {
    return {
    };
  }

  // 所有更新this.className的统一调用此处，更新class时，保持默认样式，class顺序无影响
  _updataClassName(newValue = this.className) {
    if (!this.className && !newValue) {
      this.className = defaultClassName;
      return;
    }

    if (this.className === newValue) return;

    const classArr = getSplitArr(newValue); // 拆为数组来判断，避免有包含相同字符串的情况
    const hasDefault = classArr.includes(defaultClassName);

    if (!hasDefault) {
      classArr.push(defaultClassName); // defaultClassName放最前
    }

    this.className = classArr.join(' ');
  }

  // 所有更新tthis.style.cssText的统一调用此处,新增方法：应用 style 属性
  // 需要支持默认style。（有的组件需要支持从class样式里把属性提取为style属性，如text组件）
  _updateStyle(styleString, otherProcess?) {
    if (this.isProcessingStyle) return; // 防止设置 cssText 时 attributeChangedCallback 重入
    const normalizedStyleString = normalizeDirectionalStyleText(styleString);
    if (this.lastStyleValue === normalizedStyleString) return;
    try {
      this.isProcessingStyle = true;
      this.lastStyleValue = normalizedStyleString; // 记录初始值
      this.style.cssText = normalizedStyleString;
      otherProcess && otherProcess(normalizedStyleString);
    } finally {
      this.isProcessingStyle = false;
    }
  }

  _updataInnerClassName(newValue: string = '') {
    let textElement = this.firstElementChild;
    if (!textElement) {
      // console.log('updateinner classname while no mtext child');
      textElement = this.createTextElement();
    }

    if (!textElement.className && !newValue) {
      textElement.className = `${defaultClassName} ${defaultInnerClassName}`;
      return;
    }

    if (textElement.className === newValue) return;

    const classArr = getSplitArr(newValue); // 拆为数组来判断，避免有包含相同字符串的情况
    const hasDefault = classArr.includes(defaultClassName);
    const hasInnerTextClassName = classArr.includes(innerTextClassName);
    const hasLinesClassName = classArr.includes(linesClassName);

    if (!hasDefault) {
      classArr.push(defaultClassName); // 添加默认样式
    }

    if (!hasInnerTextClassName) {
      classArr.push(innerTextClassName);
    }

    if (!hasLinesClassName) {
      classArr.push(linesClassName);
    }

    textElement.className = classArr.join(' ');
  }

  _updateInnerStyle(style: string) {
    const textElement = this.firstElementChild as HTMLElement;
    if (textElement) {
      textElement.style.cssText = this._getinnerStyleText(style);
    }
  }

  // 从容器样式生成文本元素样式
  _getinnerStyleText(outerStyleText: string = '') {
    let styleText = `${outerStyleText} ${textInnerForceStyleStr}`;
    const computedStyle = getComputedStyle(this);
    if (computedStyle.fontFamily?.includes('Bold') && computedStyle.fontWeight !== '700') {
      styleText += 'font-weight: 700 !important;';
    }
    const lineClamp = computedStyle['-webkit-line-clamp'];
    let realLineClamp = lineClamp;
    if (lineClamp === 'none') realLineClamp = 1;
    // if (lineClamp === '0' || lineClamp === 0 || lineClamp === 'none') {
    // if (lineClamp === '999999999999999') {
    if (lineClamp === '2e+09' || lineClamp === 2000000000 || lineClamp === '2000000000') {
      // 样式属性最大支持：2.14748e+09, 故取2e+09
      // maxLinesTransformer.js中对0 和 none设置为unset; 刚初始化，未挂载时，为空字符串。
      // -webkit-line-clamp未设置（空）、none、0，等都会默认值none，代表不限制行数。
      // 但Text的默认行数为1。如何准确区分是要限制1行，还是不限制行
      // 空值给1。  0或none给none
      realLineClamp = 'unset';
    }
    styleText += `-webkit-line-clamp: ${realLineClamp};`;
    return styleText;
  }

  initContent() {
    const content = this.getAttribute('content');
    if (content) {
      this.textContent = content;
    }
  }
}

export default Text;
